import eventMixin from '../lib/mixins/event';
import Logger from '../lib/logger';
import Client from 'shopify-buy';
import { getBundleId, getCDNBundleSettings, initRecharge, loginShopifyAppProxy, validateBundle } from '@rechargeapps/storefront-client';

// Workflow: https://storefront.rechargepayments.com/client/docs/methods/bundle/overview/#example-static-bundle-workflow

// https://storefront.rechargepayments.com/client/docs/examples/bundle/fixed_price_bundle/
export default class ProductBlockBundleWidget {

	constructor(dom) {

		// Vars
		// ▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒
		this.NAME = `ProductBlockBundleWidget`;

		this.dom = dom;

		this.bundleDropdownsAreaDom = this.dom.querySelector(`[data-product-block-bundle-widget-dropdowns]`);
		this.bundleLineItemProperties = {};
		this.bundleProductContainerDataAttribute = `data-product-block-bundle-widget-product`;
		this.bundleProductOptionDropdownDataAttribute = `data-product-block-bundle-widget-dropdown`;
		this.bundleSettings;

		this.priceDom = this.dom.querySelector(`[data-product-block-bundle-widget-price]`);
		this.pricePreviewDoms = this.dom.querySelectorAll(`[data-product-block-bundle-widget-price-preview]`);

		this.productJson = JSON.parse(this.dom.getAttribute(`data-product`));

		this.rbId;

		this.rechargeSession;

		this.sellingPlanRadioDoms = this.dom.querySelectorAll(`[data-product-block-bundle-widget-selling-plan]`);

		this.shopifyAccessToken = `4a5b2b5d4f4165eecef2668673e3f5d8`;
		this.shopifyClient;
		this.shopifyCollections;
		this.shopifyProductId = this.dom.getAttribute(`data-product-block-bundle-widget`);
		this.shopifyStoreUrl = `betterdays-co.myshopify.com`;

		/**
		 * //Bundle Object:
			const bundle = {
				externalVariantId: string; // variant selected from bundle data
				externalProductId: string; // bundle product id
				selections: {
					collectionId: string; // collection_id from variant - shopify collection id
					externalProductId: string; // product id from above shopify collection
					externalVariantId: string; // variant id from above product
					quantity: number; // number of items - must obey option_source quanity_min/quanity_max if defined
				}[];
			};
		 */
		this.bundle;


	}

	// Logic
	// ▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒

	async init() {
		Logger.info(`${this.NAME} > init`);

		const self = this;

		// Initializing a client to return content in the store's primary language
		this.shopifyClient = Client.buildClient({
			domain: this.shopifyStoreUrl,
			storefrontAccessToken: this.shopifyAccessToken
		});

		// Initialize the Recharge SDK
		await initRecharge({
			storeIdentifier: this.shopifyStoreUrl,
			storefrontAccessToken: `strfnt_7b18e516ed81e2e6b9aadbf4592a21c40f50744540f9297214bb4a90ccdd590a`,
			loginRetryFn: async () => {
				const session = await loginShopifyAppProxy();
				self.rechargeSession = session;
				return session;
			}
		});
		this.rechargeSession = await loginShopifyAppProxy();

		/**
		 * Let's start building the bundle payload that will ultimately be used
		 * to add a set of bundle selections to the cart.
		 */

		const bundleSettings = await this.getBundleSettings();

		this.bundle = {};

		// Add the external product id
		this.bundle.externalProductId = bundleSettings.external_product_id;

		// Add the external variant id
		this.bundle.externalVariantId = bundleSettings.variants[0].external_variant_id;

		// Setup up empty selections
		this.bundle.selections = [];

		await this.addProductOptionDropdowns();

		this.compileProductOptionSelectionsIntoBundle();

		this.adjustPricePreview();

		// Listen for selling plan radio clicks
		this.sellingPlanRadioDoms.forEach((dom) => {
			dom.addEventListener(`click`, (e) => {
				self.adjustPricePreview();
			});
		});

		// Listen for a change in a dropdown
		this.dom.querySelectorAll(`[${self.bundleProductOptionDropdownDataAttribute}]`).forEach((dropdown) => {
			dropdown.addEventListener('change', (e) => {
				self.compileProductOptionSelectionsIntoBundle();
			});
		});

		// Listen for an add to cart click
		document.getElementById('addToCart').addEventListener('click', async () => {
			self.addToCart();
		});
	}

	async addToCart() {

		const sellingPlanId = this.getSelectedSellingPlanId();

		const isBundleValid = await validateBundle(this.bundle);
		if (isBundleValid !== true) {
			return false;
		}

		const rbId = await getBundleId(this.bundle);
		this.rbId = rbId;

		// Adds the value directly to the cart
		const cartData = {
			items: [{
				id: this.bundle.externalVariantId,
				quantity: 1,
				selling_plan: (sellingPlanId == 'one-time') ? null : sellingPlanId,
				properties: {
					...this.bundleLineItemProperties,
					_rb_id: this.rbId
				},
			}]
		};
		await fetch(window.Shopify.routes.root + 'cart/add.js', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(cartData),
		});

		window.location.href = '/cart';
	}

	/**
	 * addProductOptionDropdowns
	 * Add a vertical list of variant selectors for all products in the bundle
	 */
	async addProductOptionDropdowns() {
		Logger.info(`${this.NAME} > addProductOptionDropdowns`);

		const self = this;

		/**
		 * For each collection:
		 * - make a dropdown, and this dropdown contains every product in the collection
		 * - show the dropdown X times
		 */

		const collections = await this.getCollectionsWithProductsForBundleSettings();

		const collectionsToForceToOneDropdown = [
			`gid://shopify/Collection/500511015211`,
		];

		collections.forEach((collection) => {

			const collectionShouldBeLimitedToOneDropdown = collectionsToForceToOneDropdown.includes(collection.id);

			const allowedQuantity = collection.allowed_quantity ? collection.allowed_quantity : this.bundleSettings.variants[this.bundleSettings.default_bundle_variant_id].items_count;

			const label = collections.length > 1 ? collection.title : 'Flavors';
			const instructions = allowedQuantity > 1 && !collectionShouldBeLimitedToOneDropdown ? `<div class="product__label-instructions">Select ${allowedQuantity} flavors</div>` : '';

			let html = `<div ${self.bundleProductContainerDataAttribute}="${collection.title}" style="padding:12px 0;">
                <div class="product__label-wrapper">
                    <label class="product__label" for="${collection.id}">${label}</label>
										${instructions}
                </div>`;


			for (let quantityIdx = 0; quantityIdx < allowedQuantity; quantityIdx++) {

				// If this collection should be forced to one dropdown, and if this is after the first dropdown, then omit
				let productOptionClass = '';
				if (collectionShouldBeLimitedToOneDropdown && quantityIdx > 0) {
					productOptionClass = ' product__option--hidden';
				}

				if (allowedQuantity == 1 && collection.available_products.length == 1 && collection.available_products[0].variants.length == 1) {
					// No selection required, so use a hidden input and a message
					html += `
					<div class="product__option${productOptionClass}">
						<input type="hidden" name="${collection.id}[${quantityIdx}]" value="${collection.id}-${collection.available_products[0].id}-${collection.available_products[0].variants[0].id}" ${self.bundleProductOptionDropdownDataAttribute} data-line-item-value="Included">
						<div class="product__option-message">This bundle includes ${collection.available_products[0].title}</div>
					</div>
					`;
				} else {

					html += `
                <div class="product__option${productOptionClass}">
                    <div class="select-wrapper">
                        <select id="${collection.id}-${quantityIdx}" name="${collection.id}[${quantityIdx}]" class="input dynamic-variant-input" ${self.bundleProductOptionDropdownDataAttribute}>`;
					const products = collection.available_products;
					products.forEach((product, idx) => {
						product.variants.forEach((variant) => {
							const optionLabel = (allowedQuantity == 1 && product.variants.length == 1) ? `This bundle includes ${collection.products[0].title}` : variant.title;
							html += `<option value="${collection.id}-${product.id}-${variant.id}">${optionLabel}</option>`;
						});
					});
					html += `</select>
                        <span class="icon icon-new icon-chevron-small ">
                            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" role="presentation"><path d="M1.875 7.438 12 17.563 22.125 7.438" stroke="currentColor" stroke-width="2.75"></path></svg>
                        </span>
                    </div>
                </div>`;
				}
			}

			self.bundleDropdownsAreaDom.innerHTML += html;
		});
	}

	/**
	 * adjustPricePreview - Adjust the preview of the price
	 */
	adjustPricePreview() {
		Logger.info(`${this.NAME} > adjustPricePreview`);
		if (!this.productJson) return;
		if (!this.priceDom) return;

		/**
		 * Set the prices in the plan selection widget
		 */

		this.pricePreviewDoms.forEach((pricePreviewDom) => {
			const type = pricePreviewDom.getAttribute(`data-product-block-bundle-widget-price-preview`);
			if (type == 'one-time') {
				if (this.productJson.compare_at_price && this.productJson.compare_at_price > this.productJson.price) {
					pricePreviewDom.innerHTML = `<s>$${(this.productJson.compare_at_price / 100).toFixed(2)}</s> $${(this.productJson.price / 100).toFixed(2)}`;
				} else {
					pricePreviewDom.innerHTML = `$${(this.productJson.price / 100).toFixed(2)}`;
				}
			} else {
				// console.log(this.productJson);
				const variant = this.productJson.variants[0];
				const sellingPlanGroup = this.productJson.selling_plan_groups[0];
				const sellingPlanAllocation = variant.selling_plan_allocations[0];
				// if (sellingPlanAllocation.selling_plan_id == type) {

				if (this.productJson.compare_at_price && this.productJson.compare_at_price > this.productJson.price) {
					sellingPlanGroup.selling_plans.forEach((sellingPlan) => {
						const priceAdjustment = sellingPlan.price_adjustments[0];
						const priceAdjustmentAmount = (priceAdjustment.value / 100);
						const originalSubscriptionPrice = this.productJson.compare_at_price * (1 - priceAdjustmentAmount);
						pricePreviewDom.innerHTML = `<s>$${(originalSubscriptionPrice / 100).toFixed(2)}</s> $${(sellingPlanAllocation.price / 100).toFixed(2)}`;
					});
				} else {
					pricePreviewDom.innerHTML = `$${(sellingPlanAllocation.price / 100).toFixed(2)}`;
				}

				// }
			}
		});

		/**
		 * Update for the selected selling plan
		 */

		const sellingPlanId = this.getSelectedSellingPlanId();

		let price = null;

		// If its a one time purchase, find the normal price
		// The price is the price of the main bundle (not a sub-project)
		if (sellingPlanId == 'one-time') {
			price = this.productJson.price;

			// If the price is a selling plan, we need to find the selling plan price.
			// The price is associated with a variant. This product bundle should have a single
			// variant
		} else {
			const variant = this.productJson.variants[0];
			const sellingPlanAllocation = variant.selling_plan_allocations[0];
			price = sellingPlanAllocation.price;
		}

		this.priceDom.innerHTML = `$${(price / 100).toFixed(2)}`;

		if (!price) {
			this.priceDom.classList.add(`product-block-bundle-widget__price--hidden`);
		} else {
			this.priceDom.classList.remove(`product-block-bundle-widget__price--hidden`);
		}

	}

	/**
	 * Gather all selections in the dropdowns, turn them into variants,
	 * and then create a bundle
	 * https://shopify.github.io/js-buy-sdk/ProductHelpers.html
	 */
	async compileProductOptionSelectionsIntoBundle() {
		Logger.info(`${this.NAME} > compileProductOptionSelectionsIntoBundle`);

		const self = this;

		// Reset the bundle selections entirely
		this.rbId = null;
		this.bundleLineItemProperties = {};
		this.bundle.selections = [];

		const collections = await this.getCollectionsWithProductsForBundleSettings();

		/**
		 * Loop through each product and get the dropdown values
		 * for each option.
		 */
		const productContainers = Array.from(this.dom.querySelectorAll(`[${this.bundleProductContainerDataAttribute}]`));

		for (let idx in productContainers) {
			const productContainer = productContainers[idx];
			const dropdowns = productContainer.querySelectorAll(`[${this.bundleProductOptionDropdownDataAttribute}]`);
			const collectionTitle = productContainer.getAttribute(this.bundleProductContainerDataAttribute);
			const lineItemValues = [];

			dropdowns.forEach((dropdown) => {

				const dropdownValueParts = dropdown.value.split('-');
				const collectionId = dropdownValueParts[0];
				const productId = dropdownValueParts[1];
				const variantId = dropdownValueParts[2];

				this.bundle.selections.push({
					"collectionId": this.getIdFromGid(collectionId),
					"externalProductId": this.getIdFromGid(productId),
					"externalVariantId": this.getIdFromGid(variantId),
					"quantity": 1
				});

				// If there is no data-line-item-value attribute, then get the text of the selected option
				if (!dropdown.getAttribute('data-line-item-value')) {
					const options = dropdown.getElementsByTagName("option");
					const optionText = options[dropdown.selectedIndex].innerHTML;
					lineItemValues.push(optionText);
				} else {
					lineItemValues.push(dropdown.getAttribute('data-line-item-value'));
				}
			});

			this.bundleLineItemProperties[collectionTitle] = lineItemValues.join(', ');

		}

		return true;
	}

	/**
	 * getBundleSettings
	 * Get a configuration object for a Recharge bundle, from Recharge
	 * https://storefront.rechargepayments.com/client/docs/methods/cdn/bundle/#getCDNbundlesettings
	 * @returns object
	 */
	async getBundleSettings() {
		Logger.info(`${this.NAME} > getBundleSettings`);
		if (this.bundleSettings) return this.bundleSettings;

		this.bundleSettings = await getCDNBundleSettings(this.shopifyProductId);
		return this.bundleSettings;
	}

	/**
	 * getCollectionIdsForBundleSettings
	 * Get an array of Collection ids configured for a bundle in Recharge
	 * @returns Array
	 */
	async getCollectionIdsForBundleSettings() {
		Logger.info(`${this.NAME} > getBundleSettings`);

		const bundleSettings = await this.getBundleSettings();

		const collectionIds = [];
		bundleSettings.variants[0].option_sources.forEach((optionSource) => {
			collectionIds.push({
				id: optionSource.collection_id,
				quantity_max: optionSource.quantity_max,
				quantity_min: optionSource.quantity_min
			});
		});

		return collectionIds;
	}

	/**
	 * getCollectionsWithProductsForBundleSettings
	 * Get all the products for a bundle, organized by Collection
	 * https://shopify.github.io/js-buy-sdk/
	 */
	async getCollectionsWithProductsForBundleSettings() {
		Logger.info(`${this.NAME} > getCollectionsWithProductsForBundleSettings`);

		if (this.shopifyCollections) return this.shopifyCollections;

		const self = this;

		const collectionIds = await this.getCollectionIdsForBundleSettings();

		// Build all products by collection
		const collections = [];
		for (let idx in collectionIds) {
			const collectionId = collectionIds[idx].id;
			const collectionGid = `gid://shopify/Collection/${collectionId}`;
			const collection = await self.shopifyClient.collection.fetchWithProducts(collectionGid, { productsFirst: 20 });
			collection['available_products'] = collection['products'].filter((product) => {
				return product.availableForSale && product.onlineStoreUrl != null;
			});
			collection['allowed_quantity'] = collectionIds[idx].quantity_max;
			collections.push(collection);
		}

		this.shopifyCollections = collections;

		return this.shopifyCollections;

	}

	/**
	 * getIdFromGid
	 * Get the ID integer from a GID string
	 */
	getIdFromGid(gid) {
		Logger.info(`${this.NAME} > getIdFromGid`);
		const gidParts = gid.split(`/`);
		return gidParts.pop();
	}

	/**
	 * getSelectedSellingPlanId
	 * Get the selected selling plan ID
	 */
	getSelectedSellingPlanId() {
		Logger.info(`${this.NAME} > getSelectedSellingPlanId`);

		for (let i = 0; i < this.sellingPlanRadioDoms.length; i++) {
			if (this.sellingPlanRadioDoms[i].checked)
				return this.sellingPlanRadioDoms[i].value;
		}

		return false;
	}
}

// Mixins
Object.assign(ProductBlockBundleWidget.prototype, eventMixin);
